import React, { FC, useCallback, useRef, useState } from 'react';
import { Alert, Button, Modal, Row, message } from 'antd';
import {
	CloudDownloadOutlined,
	CloudUploadOutlined,
	EditOutlined,
	PlusCircleFilled,
	WarningOutlined,
} from '@ant-design/icons';
import {
	ImportField,
	ImportRow,
	ColumnMapping,
	FieldValueMapping,
	FieldValueMappingEntry,
} from './ImportGridTypes';
import { parseCsvFile } from './csvParser';
import ColumnMapper from './ColumnMapper';
import FieldValueMapper from './FieldValueMapper';
import EditableImportGrid from './EditableImportGrid';
import { isRowFullyEmpty, validateRows } from './validateRows';
import useMountEffect from '../../hooks/useMountEffect';
import ButtonWithTimer from '../common/ButtonWithTimer';
import { WARNING_BUTTON_WAIT_TIME } from '../../utils/DataConstants';

type Step = 'sample' | 'upload' | 'map' | 'fieldValues' | 'edit';

interface ImportGridModalProps {
	visible: boolean;
	title?: string;
	fields: ImportField[];
	initialData?: Record<string, any>[];
	skipUpload?: boolean;
	recordHook?: (record: any) => any;
	validateRecordsOnSubmit?: (data: any[]) => string | undefined | null | false;
	onDataLoad: (result: { validData: any[] }) => Promise<any>;
	onCancel: () => void;
	onComplete?: () => void;
	// Sample-step config. When `onDownloadSample` is provided, the modal opens
	// on a 'sample' step that offers the legacy download + upload + manual
	// entry choices in a single screen; without it, the modal starts on the
	// upload step (the two-box choice). Mirrors the props previously held by
	// the wrapper `OWFlatfileButton`'s pre-modal.
	onDownloadSample?: () => void;
	// Spinner on the Download Sample button while the consumer builds the file.
	downloadingSample?: boolean;
	sampleModalText?: string;
	sampleButtonText?: string;
	showWarningMessage?: boolean;
	entityName?: string;
	// Whether the user can add rows in the edit grid. Defaults to true; set false for a fixed row
	// set (e.g. editing a value per existing record).
	allowAddRows?: boolean;
	// Whether the user can delete rows in the edit grid. Defaults to true; set false for a fixed
	// row set where removing a record makes no sense in context.
	allowDeleteRows?: boolean;
}

const buildMappedData = (
	csvData: Record<string, string>[],
	mappings: ColumnMapping[],
	fields: ImportField[]
): Record<string, any>[] =>
	csvData.map((csvRow) => {
		const row: Record<string, any> = {};
		mappings.forEach((mapping) => {
			if (mapping.fieldKey) {
				row[mapping.fieldKey] =
					csvRow[mapping.csvHeader] !== undefined ? csvRow[mapping.csvHeader] : '';
			}
		});
		// Trim strings
		fields.forEach((field) => {
			if (typeof row[field.key] === 'string') {
				row[field.key] = row[field.key].trim();
			}
		});
		// Booleans
		fields.forEach((field) => {
			if (field.type === 'boolean' && row[field.key] !== undefined && row[field.key] !== '') {
				const raw = String(row[field.key]).trim().toLowerCase();
				row[field.key] = raw === 'true' || raw === '1' || raw === 'yes';
			}
		});
		// Numbers
		fields.forEach((field) => {
			if (field.type === 'number' && row[field.key] !== undefined && row[field.key] !== '') {
				const parsed = Number(row[field.key]);
				if (!isNaN(parsed)) {
					row[field.key] = parsed;
				}
			}
		});
		return row;
	});

const computeFieldValueMappings = (
	data: Record<string, any>[],
	fields: ImportField[]
): FieldValueMapping[] => {
	const enumFields = fields.filter(
		(field) =>
			field.type === 'enum' &&
			field.config &&
			field.config.options &&
			field.config.options.length > 0
	);

	const result: FieldValueMapping[] = [];

	enumFields.forEach((field) => {
		const valueCounts: Record<string, number> = {};
		data.forEach((row) => {
			const val = row[field.key];
			if (val !== undefined && val !== null && val !== '') {
				const strVal = String(val).trim();
				if (strVal) {
					valueCounts[strVal] = (valueCounts[strVal] || 0) + 1;
				}
			}
		});

		const entries: FieldValueMappingEntry[] = [];

		Object.keys(valueCounts).forEach((csvValue) => {
			const rawVal = csvValue.toLowerCase();
			const matched = field.config!.options.find(
				(option) =>
					String(option.label).toLowerCase() === rawVal ||
					String(option.value).toLowerCase() === rawVal
			);

			if (matched) {
				entries.push({
					csvValue: csvValue,
					mappedValue: matched.value,
					autoMatched: true,
					count: valueCounts[csvValue],
				});
			} else {
				entries.push({
					csvValue: csvValue,
					mappedValue: null,
					autoMatched: false,
					count: valueCounts[csvValue],
				});
			}
		});

		if (entries.length > 0) {
			result.push({
				fieldKey: field.key,
				fieldLabel: field.label,
				options: field.config!.options,
				entries: entries,
			});
		}
	});

	return result;
};

const applyEnumMappings = (
	data: Record<string, any>[],
	fields: ImportField[],
	userMappings: FieldValueMapping[]
): Record<string, any>[] => {
	// Build lookup: fieldKey -> { csvValue (lowered) -> enumOptionValue }
	const lookups: Record<string, Record<string, string | number>> = {};
	userMappings.forEach((fieldMapping) => {
		const map: Record<string, string | number> = {};
		fieldMapping.entries.forEach((entry) => {
			if (entry.mappedValue !== null) {
				map[entry.csvValue.toLowerCase()] = entry.mappedValue;
			}
		});
		lookups[fieldMapping.fieldKey] = map;
	});

	return data.map((row) => {
		const newRow = Object.assign({}, row);
		fields.forEach((field) => {
			if (field.type === 'enum' && field.config && field.config.options && newRow[field.key]) {
				const rawVal = String(newRow[field.key]).trim();
				const rawValLower = rawVal.toLowerCase();

				// Check user mappings first
				if (lookups[field.key] && lookups[field.key][rawValLower] !== undefined) {
					newRow[field.key] = lookups[field.key][rawValLower];
				} else {
					// Fall back to auto-matching
					const matched = field.config.options.find(
						(option) =>
							String(option.label).toLowerCase() === rawValLower ||
							String(option.value).toLowerCase() === rawValLower
					);
					if (matched) {
						newRow[field.key] = matched.value;
					}
				}
			}
		});
		return newRow;
	});
};

const extractCleanData = (rows: ImportRow[]): Record<string, any>[] =>
	rows.map((row) => {
		const clean: Record<string, any> = {};
		Object.keys(row).forEach((key) => {
			if (key !== '_rowId' && key !== '_errors') {
				clean[key] = row[key];
			}
		});
		return clean;
	});

const ImportGridModal: FC<ImportGridModalProps> = ({
	visible,
	title,
	fields,
	initialData,
	skipUpload,
	recordHook,
	validateRecordsOnSubmit,
	onDataLoad,
	onCancel,
	onComplete,
	onDownloadSample,
	downloadingSample,
	sampleModalText,
	sampleButtonText,
	showWarningMessage,
	entityName,
	allowAddRows = true,
	allowDeleteRows = true,
}) => {
	const initialStep: Step =
		skipUpload && initialData ? 'edit' : onDownloadSample ? 'sample' : 'upload';
	const [step, setStep] = useState<Step>(initialStep);
	const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
	const [csvData, setCsvData] = useState<Record<string, string>[]>([]);
	const [rows, setRows] = useState<ImportRow[]>([]);
	const [mappedData, setMappedData] = useState<Record<string, any>[]>([]);
	const [fieldValueMappings, setFieldValueMappings] = useState<FieldValueMapping[]>([]);
	const [submitting, setSubmitting] = useState(false);
	// Tracks whether the user has touched the data (edited/added/deleted grid rows
	// or uploaded a file). Drives the "discard changes?" confirmation on close —
	// loading initial data or navigating import steps does not flip this, only
	// genuine user input does. Reset on a successful submit.
	const [dirty, setDirty] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const rowIdRef = useRef(1);

	const nextRowId = () => rowIdRef.current++;

	const buildEmptyImportRow = (): ImportRow => {
		const row: ImportRow = { _rowId: nextRowId(), _errors: {} };
		fields.forEach((field) => {
			row[field.key] = field.type === 'boolean' ? false : '';
		});
		return row;
	};

	const toImportRows = (data: Record<string, any>[]): ImportRow[] =>
		data.map((item) => {
			const row: ImportRow = { _rowId: nextRowId(), _errors: {} };
			fields.forEach((field) => {
				row[field.key] = item[field.key] !== undefined ? item[field.key] : '';
			});
			return row;
		});

	const runInitialValidation = useCallback(
		(importRows: ImportRow[]): ImportRow[] => validateRows(importRows, fields, recordHook),
		[fields, recordHook]
	);

	// Run initial validation for skipUpload + initialData path
	useMountEffect(() => {
		if (skipUpload && initialData && rows.length === 0) {
			setRows(runInitialValidation(toImportRows(initialData)));
		}
	});

	const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files && e.target.files[0];
		if (!file) return;
		try {
			const result = await parseCsvFile(file);
			if (result.headers.length === 0) {
				message.error('CSV file appears to be empty or has no headers.');
				return;
			}
			setCsvHeaders(result.headers);
			setCsvData(result.data);
			setDirty(true);
			setStep('map');
		} catch (err) {
			message.error(`Failed to parse CSV: ${err}`);
		}
	}, []);

	const handleMappingComplete = useCallback(
		(mappings: ColumnMapping[]) => {
			const data = buildMappedData(csvData, mappings, fields);
			const fvMappings = computeFieldValueMappings(data, fields);

			setMappedData(data);

			const hasAnyUnmatched = fvMappings.some((fm) =>
				fm.entries.some((entry) => !entry.autoMatched)
			);

			if (hasAnyUnmatched) {
				setFieldValueMappings(fvMappings);
				setStep('fieldValues');
			} else {
				// Every enum value auto-matched (or no enum values present) — skip the
				// confirmation step but still resolve labels to option values via
				// applyEnumMappings' fallback path.
				const resolved = applyEnumMappings(data, fields, []);
				setRows(runInitialValidation(toImportRows(resolved)));
				setStep('edit');
			}
		},
		[csvData, fields, runInitialValidation] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const handleFieldValueMappingComplete = useCallback(
		(confirmedMappings: FieldValueMapping[]) => {
			const resolved = applyEnumMappings(mappedData, fields, confirmedMappings);
			setRows(runInitialValidation(toImportRows(resolved)));
			setStep('edit');
		},
		[mappedData, fields, runInitialValidation] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const handleSubmit = useCallback(async () => {
		// Drop any fully-empty rows (e.g. an unused "Add Row" / the initial manual
		// row left blank) so they never reach the backend.
		const submittableRows = rows.filter((row) => !isRowFullyEmpty(row, fields));

		const hasFieldErrors = submittableRows.some((row) =>
			Object.values(row._errors || {}).some((errs) => errs.length > 0)
		);

		const validData = extractCleanData(submittableRows);

		let batchError: string | undefined | null | false = null;
		if (validateRecordsOnSubmit) {
			batchError = validateRecordsOnSubmit(validData);
		}

		if (hasFieldErrors || batchError) {
			if (hasFieldErrors) message.error('Fix validation errors before submitting.');
			if (batchError) message.error(batchError);
			return;
		}

		setSubmitting(true);
		try {
			const result = await onDataLoad({ validData });
			message.success(result || 'Data submitted successfully!');
			setDirty(false);
			if (onComplete) onComplete();
		} catch (err) {
			// Re-run validation so the consumer's recordHook can surface anything
			// it collected during submit (e.g., backend per-row errors stored in
			// a ref). Constraint and recordHook errors land on rows via the
			// standard `_errors` mechanism.
			setRows((current) => validateRows(current, fields, recordHook));
			// A falsy rejection signals a silent cancel (e.g. the consumer showed a confirm dialog
			// and the user dismissed it). Keep the grid open without surfacing an error toast.
			if (err) {
				message.error(typeof err === 'string' ? err : 'Submission failed.');
			}
		} finally {
			setSubmitting(false);
		}
	}, [rows, validateRecordsOnSubmit, onDataLoad, onComplete, fields, recordHook]);

	const handleRowsChange = useCallback((updatedRows: ImportRow[]) => {
		setRows(updatedRows);
		setDirty(true);
	}, []);

	// Intercept every close path (Cancel button, Escape, the X, mask click). When
	// the user has unsaved changes, confirm before discarding; otherwise close
	// straight through.
	const handleRequestClose = useCallback(() => {
		if (!dirty) {
			onCancel();
			return;
		}
		Modal.confirm({
			title: 'Discard changes?',
			content: 'You have unsaved changes. If you close now, your changes will be lost.',
			okText: 'Discard changes',
			okType: 'danger',
			cancelText: 'Keep editing',
			onOk: onCancel,
		});
	}, [dirty, onCancel]);

	const handleManualEntry = useCallback(() => {
		// Manual entry starts with a single blank row to type into; the user adds
		// more via "Add Row" (which now toasts and scrolls the new row into view).
		setRows(runInitialValidation([buildEmptyImportRow()]));
		setStep('edit');
	}, [fields, runInitialValidation]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleUploadAreaClick = useCallback(() => {
		if (fileInputRef.current) fileInputRef.current.click();
	}, []);

	const handleProceedToUpload = useCallback(() => {
		// Skip the upload-step UI entirely — open the native file picker
		// straight away. If the user cancels, they stay on the sample step and
		// can hit Proceed again.
		if (fileInputRef.current) fileInputRef.current.click();
	}, []);

	const modalWidth = step === 'edit' ? '90vw' : step === 'fieldValues' ? 700 : 600;

	// On the edit step the grid should fill the screen, so pin the modal body to a
	// tall fixed height (90vh minus the title + footer + body padding chrome) and
	// make it a flex column. EditableImportGrid stretches to fill it (h-full /
	// flex-1) and scrolls internally. Other steps keep antd's default content-sized
	// body. `centered` keeps the tall modal vertically within the viewport.
	const editBodyStyle: React.CSSProperties = {
		height: 'calc(90vh - 108px)',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	};

	return (
		<Modal
			visible={visible}
			title={title || 'Import Data'}
			width={modalWidth}
			centered={step === 'edit'}
			bodyStyle={step === 'edit' ? editBodyStyle : undefined}
			onCancel={handleRequestClose}
			footer={
				step === 'edit' ? (
					<>
						<Button onClick={handleRequestClose}>Cancel</Button>
						<Button type="primary" onClick={handleSubmit} loading={submitting}>
							Submit
						</Button>
					</>
				) : null
			}
			destroyOnClose
		>
			{step === 'sample' && (
				<>
					<Row>{sampleModalText || 'Do you want to download a sample file?'}</Row>
					{showWarningMessage ? (
						<>
							<div className="mt-2">
								<Alert
									message="You will lose data if you do not read this carefully."
									type="error"
								/>
							</div>
							<div className="mt-4 flex flex-row items-center">
								<WarningOutlined translate="" />
								<div className="ml-2 text-gray-400">
									{`Please ensure that you always upload the entire ${
										entityName || 'data'
									} list. You can download the list here to make updates, addition/deletion.`}
								</div>
							</div>
						</>
					) : null}
					<Row>
						<Button
							type="primary"
							className="mt-6 w-full"
							loading={downloadingSample}
							onClick={onDownloadSample}
							icon={<CloudDownloadOutlined translate="" />}
						>
							{sampleButtonText || 'Download Sample File'}
						</Button>
					</Row>
					<Row>
						{showWarningMessage ? (
							<ButtonWithTimer
								icon={<PlusCircleFilled translate="" />}
								className="mt-3 w-full"
								disableUntilSeconds={WARNING_BUTTON_WAIT_TIME}
								onClick={handleProceedToUpload}
							>
								Proceed to Upload
							</ButtonWithTimer>
						) : (
							<Button
								icon={<PlusCircleFilled translate="" />}
								className="mt-3 w-full"
								onClick={handleProceedToUpload}
							>
								Proceed to Upload
							</Button>
						)}
					</Row>
					<Row>
						<Button
							icon={<EditOutlined translate="" />}
							className="mt-3 w-full"
							onClick={handleManualEntry}
						>
							Enter Manually
						</Button>
					</Row>
				</>
			)}

			{/*
			 * File input lives outside the upload step so handleProceedToUpload
			 * (which fires from the sample step) can invoke .click() without
			 * waiting for the upload step to mount.
			 */}
			<input
				ref={fileInputRef}
				type="file"
				accept=".csv"
				className="hidden"
				onChange={handleFileSelect}
			/>

			{step === 'upload' && (
				<div className="flex gap-4">
					<div
						className="flex-1 cursor-pointer rounded-lg border-2 border-dashed border-owgray px-5 py-10 text-center"
						onClick={handleUploadAreaClick}
					>
						<CloudUploadOutlined className="text-5xl text-[#999]" />
						<div className="mt-3 text-[#666]">Upload CSV</div>
					</div>
					{!onDownloadSample && (
						<div
							className="flex-1 cursor-pointer rounded-lg border-2 border-dashed border-owgray px-5 py-10 text-center"
							onClick={handleManualEntry}
						>
							<EditOutlined className="text-5xl text-[#999]" />
							<div className="mt-3 text-[#666]">Enter Manually</div>
						</div>
					)}
				</div>
			)}

			{step === 'map' && (
				<ColumnMapper
					csvHeaders={csvHeaders}
					fields={fields}
					onMappingComplete={handleMappingComplete}
				/>
			)}

			{step === 'fieldValues' && (
				<FieldValueMapper
					fieldValueMappings={fieldValueMappings}
					onConfirm={handleFieldValueMappingComplete}
				/>
			)}

			{step === 'edit' && (
				<EditableImportGrid
					fields={fields}
					rows={rows}
					onRowsChange={handleRowsChange}
					recordHook={recordHook}
					allowAddRows={allowAddRows}
					allowDeleteRows={allowDeleteRows}
				/>
			)}
		</Modal>
	);
};

export default ImportGridModal;
